import { useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight } from "lucide-react";
import { SoftwareUpdateWizardState, MODE_META, SU_PLATFORM_META } from "../types";
import { buildPayload } from "../lib/buildConfig";
import { summarize, summarizeHeader } from "../lib/summary";

interface Props {
  state: SoftwareUpdateWizardState;
  onJumpToStep: (id: "mode" | "configure" | "scope") => void;
}

export function ReviewSaveStep({ state, onJumpToStep }: Props) {
  const [showJson, setShowJson] = useState(false);
  const payload = useMemo(() => buildPayload(state), [state]);
  const lines = summarize(state);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>{state.name || "Untitled policy"}</CardTitle>
              <CardDescription>{summarizeHeader(state)}</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onJumpToStep("mode")}>
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1.5 text-sm">
            {lines.map((l, i) => (
              <li key={i} className="text-muted-foreground">• {l}</li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>Scope</CardTitle>
              <CardDescription>Devices receive this policy through shared tags.</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onJumpToStep("scope")}>
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {state.tagIds.length === 0 ? (
            <p className="text-sm text-warning">
              No tags selected — the policy will be created but reach no devices until tagged.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {state.tagIds.map((id) => (
                <Badge key={id} variant="secondary" className="font-normal">{id}</Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              onClick={() => setShowJson((v) => !v)}
              aria-expanded={showJson}
            >
              {showJson ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              JSON payload sent to Apple
            </button>
          </CardTitle>
          <CardDescription>
            Kind: <code className="font-mono text-xs">{payload.kind}</code> · Identifier:{" "}
            <code className="font-mono text-xs">{payload.identifier}</code>
          </CardDescription>
        </CardHeader>
        {showJson && (
          <CardContent>
            <pre className="text-xs bg-muted rounded-md p-3 overflow-auto max-h-80">
              {JSON.stringify(payload.config, null, 2)}
            </pre>
          </CardContent>
        )}
      </Card>

      <p className="text-xs text-muted-foreground">
        Save runs: <strong>validate</strong> → <strong>create policy</strong> → <strong>tag devices</strong> →{" "}
        <strong>tag policy</strong>. {MODE_META[state.mode].label} · {SU_PLATFORM_META[state.platform].label}.
      </p>
    </div>
  );
}
